/**
 * Branding utilities for domain-specific customization
 */

export interface ColorScheme {
  primary: string;
  accentBlue: string;
  accentPink: string;
  backgroundPrimary: string;
  backgroundSecondary: string;
  textPrimary: string;
  textSecondary: string;
  white: string;
}

export interface BrandingConfig {
  appName: string;
  favicon: string;
  logoIcon: string;
  isInvoicerGG: boolean;
  colors: ColorScheme;
}

// Color schemes
const DEFAULT_COLORS: ColorScheme = {
  primary: '#4F46E5',        // Default Indigo
  accentBlue: '#3B82F6',     // Default Blue
  accentPink: '#EC4899',     // Default Pink
  backgroundPrimary: '#FFFFFF', // White
  backgroundSecondary: '#F9FAFB', // Gray-50
  textPrimary: '#111827',    // Gray-900
  textSecondary: '#6B7280',  // Gray-500
  white: '#FFFFFF'           // Pure White
};

/**
 * Detects if the current domain is invoicer.gg
 */
export const isInvoicerGGDomain = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // For testing purposes, also check if URL contains a query parameter
  const urlParams = new URLSearchParams(window.location.search);
  const forceInvoicerGG = urlParams.get('theme') === 'invoicer';
  
  return window.location.hostname === 'invoicer.gg' || 
         window.location.hostname === 'www.invoicer.gg' ||
         forceInvoicerGG;
};

/**
 * Gets the branding configuration based on the current domain
 */
export const getBrandingConfig = (): BrandingConfig => {
  const isInvoicerGG = isInvoicerGGDomain();
  
  return {
    appName: isInvoicerGG ? 'Invoicer.gg' : 'WikipayIt',
    favicon: '/favicon.svg',
    logoIcon: isInvoicerGG ? 'invoice' : 'default',
    isInvoicerGG,
    // Always use default InvoiceBug colors regardless of domain
    colors: DEFAULT_COLORS
  };
};

/**
 * Updates CSS custom properties with the current color scheme
 */
export const updateCSSVariables = (colors: ColorScheme): void => {
  if (typeof document === 'undefined') return;
  
  const root = document.documentElement;
  root.style.setProperty('--brand-primary', colors.primary);
  root.style.setProperty('--brand-accent-blue', colors.accentBlue);
  root.style.setProperty('--brand-accent-pink', colors.accentPink);
  root.style.setProperty('--brand-background-primary', colors.backgroundPrimary);
  root.style.setProperty('--brand-background-secondary', colors.backgroundSecondary);
  root.style.setProperty('--brand-text-primary', colors.textPrimary);
  root.style.setProperty('--brand-text-secondary', colors.textSecondary);
  root.style.setProperty('--brand-white', colors.white);
};

/**
 * Updates the page title, favicon, and color scheme based on domain
 */
export const updatePageBranding = (): void => {
  const config = getBrandingConfig();
  
  // Update page title
  if (typeof document !== 'undefined') {
    document.title = `${config.appName} - Invoice Management System`;
    
    // Update favicon
    let favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.type = 'image/svg+xml';
      document.head.appendChild(favicon);
    }
    favicon.href = config.favicon;
    
    // Update CSS variables
    updateCSSVariables(config.colors);
  }
};
